import { useState } from 'react';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Select,
  Modal,
  Toggle,
  Tabs,
  LoadingSkeleton,
} from '@nexora/design-system';
import {
  useTaxVariables,
  useCreateTaxVariable,
  useUpdateTaxVariable,
  useDeleteTaxVariable,
  type TaxVariable,
  type TaxVariableInput,
  type TaxVariableType,
  type TaxVariableScope,
} from './useTaxVariables';
import {
  useTaxFormulas,
  useCreateTaxFormula,
  useUpdateTaxFormula,
  useDeleteTaxFormula,
  type TaxFormula,
  type TaxFormulaInput,
} from './useTaxFormulas';
import { formatDateTime } from '../../../shared/utils/format';

/* ───────────────────────────────────────────────────────────────────────────
   Portado de base-html, slug `erpone_gest_o_de_vari_veis_e_f_rmulas_fiscais`
   → Gestão de Variáveis e Fórmulas Fiscais — CRUD /fiscal/variables e
   /fiscal/formulas (FiscalConfigController).

   Da referência vieram: a lista de variáveis disponíveis (nome, tipo,
   escopo, fonte), o editor de fórmula com expressão e a validação
   ("No circular dependencies detected").

   O que o backend valida na fórmula: sintaxe via FormulaEvaluatorService
   (a resposta traz `validation: { valid, errors, variables }`). O editor
   mostra esse retorno real após salvar — e também o estado pré-salvamento
   baseado nos campos do DTO.

   Não vieram da referência (não existem no contrato): "New Tax Rule" como
   ação desta tela, nem detecção de dependência circular (o backend devolve
   apenas `valid/errors/variables` da expressão).
   ─────────────────────────────────────────────────────────────────────────── */

const VARIABLE_TYPES: { value: TaxVariableType; label: string }[] = [
  { value: 'NUMERO', label: 'Numérico' },
  { value: 'TEXTO', label: 'Texto' },
  { value: 'BOOLEANO', label: 'Booleano' },
  { value: 'DATA', label: 'Data' },
  { value: 'MONTANTE', label: 'Montante (R$)' },
  { value: 'ALIQUOTA', label: 'Alíquota (%)' },
];

const VARIABLE_SCOPES: { value: TaxVariableScope; label: string }[] = [
  { value: 'GLOBAL', label: 'Global' },
  { value: 'TENANT', label: 'Tenant' },
  { value: 'COMPANY', label: 'Empresa' },
  { value: 'DOCUMENTO', label: 'Documento' },
  { value: 'ITEM', label: 'Item' },
];

const SOURCES = [
  { value: 'USUARIO', label: 'Manual (usuário)' },
  { value: 'SISTEMA', label: 'Sistema' },
  { value: 'CALCULADA', label: 'Calculada (expressão)' },
];

const TYPE_LABEL: Record<string, string> = {
  NUMERO: 'Numérico', TEXTO: 'Texto', BOOLEANO: 'Booleano', DATA: 'Data',
  MONTANTE: 'Montante', ALIQUOTA: 'Alíquota',
};

const SCOPE_LABEL: Record<string, string> = {
  GLOBAL: 'Global', TENANT: 'Tenant', COMPANY: 'Empresa', DOCUMENTO: 'Documento', ITEM: 'Item',
};

function emptyVariable(): TaxVariableInput {
  return { name: '', type: 'NUMERO', scope: 'TENANT', source: 'USUARIO', readOnly: false };
}

export function TaxVariablesPage(): JSX.Element {
  const [tab, setTab] = useState('variables');

  /* ── Variables state ── */
  const [varModalOpen, setVarModalOpen] = useState(false);
  const [editingVar, setEditingVar] = useState<TaxVariable | undefined>(undefined);
  const [varForm, setVarForm] = useState<TaxVariableInput>(emptyVariable());
  const [deleteTarget, setDeleteTarget] = useState<TaxVariable | null>(null);

  const variablesQuery = useTaxVariables();
  const createVar = useCreateTaxVariable();
  const updateVar = useUpdateTaxVariable();
  const deleteVar = useDeleteTaxVariable();

  /* ── Formulas state ── */
  const [formulaModalOpen, setFormulaModalOpen] = useState(false);
  const [editingFormula, setEditingFormula] = useState<TaxFormula | undefined>(undefined);
  const [formulaForm, setFormulaForm] = useState<TaxFormulaInput>({ name: '', expression: '' });
  const [deleteFormulaTarget, setDeleteFormulaTarget] = useState<TaxFormula | null>(null);

  const formulasQuery = useTaxFormulas();
  const createFormula = useCreateTaxFormula();
  const updateFormula = useUpdateTaxFormula();
  const deleteFormula = useDeleteTaxFormula();

  const variables = variablesQuery.data?.data ?? [];
  const formulas = formulasQuery.data?.data ?? [];

  const savingVariable = createVar.isPending || updateVar.isPending;
  const savingFormula = createFormula.isPending || updateFormula.isPending;

  function openVariableCreate(): void {
    setEditingVar(undefined);
    setVarForm(emptyVariable());
    setVarModalOpen(true);
  }

  function openVariableEdit(v: TaxVariable): void {
    setEditingVar(v);
    setVarForm({
      name: v.name,
      ...(v.description ? { description: v.description } : {}),
      type: v.type as TaxVariableType,
      scope: v.scope as TaxVariableScope,
      source: (v.source as TaxVariableInput['source']) ?? 'USUARIO',
      ...(v.expression ? { expression: v.expression } : {}),
      ...(v.defaultValue !== null ? { defaultValue: v.defaultValue } : {}),
      readOnly: v.readOnly,
    });
    setVarModalOpen(true);
  }

  function handleSaveVariable(): void {
    const input = { ...varForm };
    if (editingVar) {
      updateVar.mutate({ id: editingVar.id, ...input }, { onSuccess: () => setVarModalOpen(false) });
    } else {
      createVar.mutate(input, { onSuccess: () => setVarModalOpen(false) });
    }
  }

  function openFormulaCreate(): void {
    setEditingFormula(undefined);
    setFormulaForm({ name: '', expression: '' });
    setFormulaModalOpen(true);
  }

  function openFormulaEdit(f: TaxFormula): void {
    setEditingFormula(f);
    setFormulaForm({
      name: f.name,
      ...(f.description ? { description: f.description } : {}),
      expression: f.expression,
      enabled: f.enabled,
    });
    setFormulaModalOpen(true);
  }

  function handleSaveFormula(): void {
    if (editingFormula) {
      updateFormula.mutate({ id: editingFormula.id, ...formulaForm }, { onSuccess: () => setFormulaModalOpen(false) });
    } else {
      createFormula.mutate(formulaForm, { onSuccess: () => setFormulaModalOpen(false) });
    }
  }

  const tabs = [
    { key: 'variables', label: `${variables.length} variáveis` },
    { key: 'formulas', label: `${formulas.length} fórmulas` },
  ];

  return (
    <div className="tax-variables-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Gestão de Variáveis e Fórmulas Fiscais
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Variáveis reutilizáveis e fórmulas validadas que alimentam o motor fiscal.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={tab === 'variables' ? openVariableCreate : openFormulaCreate}
        >
          + Nova {tab === 'variables' ? 'variável' : 'fórmula'}
        </Button>
      </div>

      <Tabs
        tabs={tabs}
        value={tab}
        onChange={setTab}
        aria-label="Variáveis e fórmulas"
        className="tax-variables-tabs"
      />

      {tab === 'variables' && (
        <Card variant="outlined" padding="0">
          <CardHeader className="px-md pt-md" action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {variablesQuery.data?.total ?? 0} variáveis
            </span>
          }>
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Variáveis disponíveis</h3>
          </CardHeader>
          {variablesQuery.isLoading ? (
            <div className="tv-loading"><LoadingSkeleton count={5} height="44px" /></div>
          ) : variablesQuery.error ? (
            <div className="tv-empty">
              <p style={{ fontWeight: 600, color: '#ba1a1a', margin: '0 0 0.5rem' }}>Erro ao carregar variáveis</p>
              <p style={{ fontSize: '0.875rem', margin: '0 0 1rem' }}>
                {variablesQuery.error instanceof Error ? variablesQuery.error.message : 'Erro desconhecido'}
              </p>
              <Button variant="primary" onClick={() => variablesQuery.refetch()}>Tentar novamente</Button>
            </div>
          ) : variables.length === 0 ? (
            <div className="tv-empty">
              <p className="m-0">Nenhuma variável cadastrada. Crie a primeira para usar em fórmulas.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="fiscal-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Tipo</th>
                    <th>Escopo</th>
                    <th>Fonte</th>
                    <th>Expressão / valor padrão</th>
                    <th>Somente leitura</th>
                    <th>Atualizado</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {variables.map((v) => (
                    <tr key={v.id}>
                      <td><strong>{v.name}</strong>{v.description && <small className="cell-sub">{v.description}</small>}</td>
                      <td><Badge size="sm" variant="neutral">{TYPE_LABEL[v.type] ?? v.type}</Badge></td>
                      <td>{SCOPE_LABEL[v.scope] ?? v.scope}</td>
                      <td>{v.source}</td>
                      <td className="cell-code">{v.expression ?? (v.defaultValue !== null ? String(v.defaultValue) : '—')}</td>
                      <td>{v.readOnly ? 'Sim' : 'Não'}</td>
                      <td className="tabular-nums">{formatDateTime(v.updatedAt)}</td>
                      <td>
                        <div className="cell-actions">
                          <button type="button" className="row-action" aria-label={`Editar ${v.name}`} title="Editar" onClick={() => openVariableEdit(v)}>
                            <span className="material-symbols-outlined" aria-hidden="true">edit</span>
                          </button>
                          <button type="button" className="row-action row-action--danger" aria-label={`Excluir ${v.name}`} title="Excluir" onClick={() => setDeleteTarget(v)}>
                            <span className="material-symbols-outlined" aria-hidden="true">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'formulas' && (
        <Card variant="outlined" padding="0">
          <CardHeader className="px-md pt-md" action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {formulasQuery.data?.total ?? 0} fórmulas
            </span>
          }>
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Fórmulas fiscais</h3>
          </CardHeader>
          {formulasQuery.isLoading ? (
            <div className="tv-loading"><LoadingSkeleton count={5} height="44px" /></div>
          ) : formulasQuery.error ? (
            <div className="tv-empty">
              <p style={{ fontWeight: 600, color: '#ba1a1a', margin: '0 0 0.5rem' }}>Erro ao carregar fórmulas</p>
              <p style={{ fontSize: '0.875rem', margin: '0 0 1rem' }}>
                {formulasQuery.error instanceof Error ? formulasQuery.error.message : 'Erro desconhecido'}
              </p>
              <Button variant="primary" onClick={() => formulasQuery.refetch()}>Tentar novamente</Button>
            </div>
          ) : formulas.length === 0 ? (
            <div className="tv-empty">
              <p className="m-0">Nenhuma fórmula cadastrada.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="fiscal-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Expressão</th>
                    <th>Variáveis</th>
                    <th>Validação</th>
                    <th>Ativa</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {formulas.map((f) => (
                    <tr key={f.id}>
                      <td><strong>{f.name}</strong>{f.description && <small className="cell-sub">{f.description}</small>}</td>
                      <td className="cell-code" style={{ maxWidth: 320 }}>{f.expression}</td>
                      <td>
                        {f.variables.length === 0 ? <span className="cell-muted">—</span> : (
                          <div className="tax-chips">
                            {f.variables.map((v) => <span key={v} className="tax-chip">{v}</span>)}
                          </div>
                        )}
                      </td>
                      <td>
                        {f.validation
                          ? <Badge size="sm" variant={f.validation.valid ? 'success' : 'error'}>{f.validation.valid ? 'Válida' : 'Inválida'}</Badge>
                          : <Badge size="sm" variant="neutral">sem validação</Badge>}
                      </td>
                      <td><Badge size="sm" variant={f.enabled ? 'success' : 'neutral'}>{f.enabled ? 'Ativa' : 'Inativa'}</Badge></td>
                      <td>
                        <div className="cell-actions">
                          <button type="button" className="row-action" aria-label={`Editar ${f.name}`} title="Editar" onClick={() => openFormulaEdit(f)}>
                            <span className="material-symbols-outlined" aria-hidden="true">edit</span>
                          </button>
                          <button type="button" className="row-action row-action--danger" aria-label={`Excluir ${f.name}`} title="Excluir" onClick={() => setDeleteFormulaTarget(f)}>
                            <span className="material-symbols-outlined" aria-hidden="true">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── Variable modal ── */}
      <Modal
        isOpen={varModalOpen}
        onClose={() => setVarModalOpen(false)}
        title={editingVar ? 'Editar variável' : 'Nova variável'}
        size="md"
      >
        <div className="modal-form">
          <Input label="Nome" value={varForm.name} onChange={(e) => setVarForm({ ...varForm, name: e.target.value })} placeholder="Ex.: ALIQUOTA_INTERNA" />
          <Input label="Descrição" value={varForm.description ?? ''} onChange={(e) => setVarForm({ ...varForm, description: e.target.value })} />
          <div className="modal-form__row">
            <Select label="Tipo" value={varForm.type} onChange={(e) => setVarForm({ ...varForm, type: e.target.value as TaxVariableType })}>
              {VARIABLE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
            <Select label="Escopo" value={varForm.scope} onChange={(e) => setVarForm({ ...varForm, scope: e.target.value as TaxVariableScope })}>
              {VARIABLE_SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
            <Select label="Fonte" value={varForm.source ?? 'USUARIO'} onChange={(e) => setVarForm({ ...varForm, source: e.target.value as TaxVariableInput['source'] })}>
              {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
          </div>
          {varForm.source === 'CALCULADA' && (
            <Input label="Expressão" value={varForm.expression ?? ''} onChange={(e) => setVarForm({ ...varForm, expression: e.target.value })} placeholder="Ex.: base * aliquota / 100" />
          )}
          {varForm.type !== 'TEXTO' && varForm.type !== 'BOOLEANO' && varForm.type !== 'DATA' && (
            <Input label="Valor padrão" type="number" step="0.01" value={varForm.defaultValue === undefined ? '' : String(varForm.defaultValue)} onChange={(e) => setVarForm({ ...varForm, defaultValue: e.target.value ? Number(e.target.value) : undefined })} />
          )}
          <div className="modal-form__toggle">
            <span>Somente leitura?</span>
            <Toggle checked={varForm.readOnly ?? false} onChange={(c) => setVarForm({ ...varForm, readOnly: c })} />
          </div>
        </div>
        <div className="modal-form__actions">
          <Button variant="ghost" onClick={() => setVarModalOpen(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSaveVariable} disabled={savingVariable || !varForm.name.trim()}>
            {savingVariable ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
        {(createVar.isError || updateVar.isError) && (
          <p style={{ color: '#ba1a1a', fontSize: '0.8125rem', margin: '0.75rem 0 0' }}>
            {(createVar.error ?? updateVar.error) instanceof Error ? (createVar.error ?? updateVar.error)?.message : 'Erro ao salvar variável'}
          </p>
        )}
      </Modal>

      {/* ── Formula modal ── */}
      <Modal
        isOpen={formulaModalOpen}
        onClose={() => setFormulaModalOpen(false)}
        title={editingFormula ? 'Editar fórmula' : 'Nova fórmula'}
        size="lg"
      >
        <div className="modal-form">
          <Input label="Nome" value={formulaForm.name} onChange={(e) => setFormulaForm({ ...formulaForm, name: e.target.value })} placeholder="Ex.: calculateICMS" />
          <Input label="Descrição" value={formulaForm.description ?? ''} onChange={(e) => setFormulaForm({ ...formulaForm, description: e.target.value })} />
          <label className="formula-label">
            Expressão (validação de sintaxe no servidor)
            <textarea
              className="formula-textarea"
              value={formulaForm.expression}
              onChange={(e) => setFormulaForm({ ...formulaForm, expression: e.target.value })}
              placeholder={'base * (aliquota / 100) + $FRETE'}
              rows={6}
              spellCheck={false}
            />
          </label>
          <div className="modal-form__toggle">
            <span>Ativa?</span>
            <Toggle checked={formulaForm.enabled ?? true} onChange={(c) => setFormulaForm({ ...formulaForm, enabled: c })} />
          </div>
        </div>
        <div className="modal-form__actions">
          <Button variant="ghost" onClick={() => setFormulaModalOpen(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSaveFormula} disabled={savingFormula || !formulaForm.name.trim() || !formulaForm.expression.trim()}>
            {savingFormula ? 'Validando…' : 'Salvar (valida no servidor)'}
          </Button>
        </div>
        {(createFormula.isError || updateFormula.isError) && (
          <p style={{ color: '#ba1a1a', fontSize: '0.8125rem', margin: '0.75rem 0 0' }}>
            {(createFormula.error ?? updateFormula.error) instanceof Error ? (createFormula.error ?? updateFormula.error)?.message : 'Erro ao salvar fórmula'}
          </p>
        )}
      </Modal>

      {/* ── Delete confirm: variable ── */}
      <Modal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Excluir variável"
        size="sm"
      >
        <p className="m-0" style={{ fontSize: '0.875rem' }}>
          Excluir <strong>{deleteTarget?.name}</strong>? Fórmulas que referenciam esta variável passam a falhar na validação.
        </p>
        <div className="modal-form__actions">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button variant="danger" onClick={() => deleteTarget && deleteVar.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) })} disabled={deleteVar.isPending}>
            {deleteVar.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </Modal>

      {/* ── Delete confirm: formula ── */}
      <Modal
        isOpen={deleteFormulaTarget !== null}
        onClose={() => setDeleteFormulaTarget(null)}
        title="Excluir fórmula"
        size="sm"
      >
        <p className="m-0" style={{ fontSize: '0.875rem' }}>
          Excluir a fórmula <strong>{deleteFormulaTarget?.name}</strong>?
        </p>
        <div className="modal-form__actions">
          <Button variant="ghost" onClick={() => setDeleteFormulaTarget(null)}>Cancelar</Button>
          <Button variant="danger" onClick={() => deleteFormulaTarget && deleteFormula.mutate(deleteFormulaTarget.id, { onSuccess: () => setDeleteFormulaTarget(null) })} disabled={deleteFormula.isPending}>
            {deleteFormula.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </Modal>

      <style>{`
        .tax-variables-page { padding: 1.5rem; }
        .tax-variables-tabs { margin-bottom: 1rem; }
        .tv-loading, .tv-empty { padding: 2rem; text-align: center; color: #74777f; }
        .tv-empty { display: grid; gap: 0.5rem; justify-items: center; }
        .modal-form { display: grid; gap: 0.75rem; }
        .modal-form__row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0.75rem; }
        .modal-form__toggle { display: flex; align-items: center; justify-content: space-between; padding: 0.5rem 0; }
        .modal-form__actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1rem; }
        .formula-label { display: flex; flex-direction: column; gap: 0.375rem; font-size: 0.8125rem; font-weight: 600; color: #43474e; }
        .formula-textarea { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.8125rem; padding: 0.625rem 0.75rem; border: 1px solid #cfd4dc; border-radius: 8px; resize: vertical; min-height: 120px; }
        .formula-textarea:focus { outline: 2px solid #002045; outline-offset: 1px; }
        .cell-code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.75rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 240px; }
        .fiscal-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .fiscal-table th {
          text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e;
          font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .fiscal-table td { padding: 0.625rem 1rem; border-bottom: 1px solid #eceef0; vertical-align: middle; }
        .fiscal-table tbody tr:hover { background: #f8fafc; }
        .tabular-nums { font-variant-numeric: tabular-nums; }
        .cell-sub { display: block; color: #74777f; font-size: 0.75rem; }
        .cell-muted { color: #74777f; }
        .cell-actions { display: flex; gap: 0.25rem; }
        .row-action { background: none; border: none; cursor: pointer; color: #43474e; padding: 0.25rem; border-radius: 6px; }
        .row-action:hover { background: #f2f4f6; }
        .row-action--danger:hover { color: #ba1a1a; background: #ffebee; }
        .tax-chips { display: inline-flex; gap: 0.25rem; flex-wrap: wrap; max-width: 18ch; }
        .tax-chip { background: #eef2f7; color: #002045; border-radius: 999px; padding: 0.125rem 0.5rem; font-size: 0.6875rem; font-weight: 600; }
        @media (max-width: 720px) { .modal-form__row { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}